// @/components/providers/LanguageContext.jsx

/**
 * LanguageContext Module
 * 
 * Provides a React Context for managing application language/locale across components.
 * Handles language changes, persistence, and event dispatching for the entire application.
 * 
 * Features:
 * - Central language state management through React Context
 * - Persistent language preference using localStorage
 * - Automatic event dispatching for language changes
 * - Debounced content refresh to prevent performance issues
 * - URL state preservation without page reloads
 * - External event listening for language changes from outside the context
 * - Simple hook API with useLanguage()
 * - Comprehensive error handling with meaningful messages
 * - Robust state synchronization between components
 * - Fallback to default language (English) when needed
 * 
 * @module components/providers/LanguageContext
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { getUserLocale, setUserLocale } from './LanguageSelector';

// Create the language context
const LanguageContext = createContext({
  locale: 'en',
  setLocale: () => { },
});

/**
 * LanguageProvider - Context provider for language/locale state
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} The provider component
 */
export const LanguageProvider = ({ children }) => {
  // IMPORTANT: Initialize with 'en' for SSR consistency to prevent hydration mismatch
  // The actual locale is set in useEffect after mount
  const [locale, setLocaleState] = useState('en');

  // Track whether locale has been initialized from user preferences
  // This prevents showing English content before the actual locale is loaded
  const [isLocaleReady, setIsLocaleReady] = useState(false);

  // Set actual locale after mount to prevent hydration mismatch
  useEffect(() => {
    const actualLocale = getUserLocale();
    if (actualLocale !== 'en') {
      setLocaleState(actualLocale);
    }
    // Mark locale as ready after we've checked user preferences
    setIsLocaleReady(true);
  }, []);

  /**
   * Set locale with proper side effects
   * 
   * @param {string} newLocale - The new locale code to set
   * @private
   */
  const setLocale = (newLocale) => {
    if (newLocale === locale) return;

    if (process.env.NODE_ENV === 'development') {
      console.log(`[DEBUG] LanguageContext.setLocale: ${locale} -> ${newLocale}`);
    }

    // Store in localStorage
    setUserLocale(newLocale);

    // Update state
    setLocaleState(newLocale);

    // Dispatch event for components not using the context
    window.dispatchEvent(
      new CustomEvent('localeChange', {
        detail: {
          locale: newLocale,
          previousLocale: locale
        }
      })
    );

    // Use debounced content refresh to prevent multiple rapid refreshes
    if (window._contentRefreshTimeout) {
      clearTimeout(window._contentRefreshTimeout);
    }

    window._contentRefreshTimeout = setTimeout(() => {
      // Dispatch a content refresh event after a short delay to allow components to update
      window.dispatchEvent(new CustomEvent('forceContentRefresh', {
        detail: { locale: newLocale }
      }));
    }, 100);

    // Update URL without reload to preserve locale in history
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      window.history.replaceState(
        { ...window.history.state, locale: newLocale },
        '',
        currentPath
      );
    }
  };

  // Listen for locale changes from outside the context
  useEffect(() => {
    /**
     * Handle locale change events from outside the context
     * 
     * @param {CustomEvent} event - The localeChange event
     * @private
     */
    const handleLocaleChange = (event) => {
      if (event.detail.locale !== locale) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[DEBUG] LanguageContext received locale change event: ${locale} -> ${event.detail.locale}`);
        }
        setLocaleState(event.detail.locale);
      }
    };

    window.addEventListener('localeChange', handleLocaleChange);

    return () => {
      window.removeEventListener('localeChange', handleLocaleChange);
    };
  }, [locale]);

  return (
    <LanguageContext.Provider value={{ locale, setLocale, isLocaleReady }}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * useLanguage - Hook to use the language context in any component
 * 
 * @returns {Object} The language context with locale and setLocale
 * @throws {Error} If used outside of a LanguageProvider
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};